import { createElement } from 'react';
import { jsPDF } from 'jspdf';
import JSZip from 'jszip';
import html2canvas from 'html2canvas';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { SharedJobPostPrintView } from '../components/SharedJobPostPrintView';
import type { SharedJobPostPdfData } from '../components/SharedJobPostPrintView';
import type { AgentApiSharedJobPostsPdfIndex } from '@/types/serializers';
import type { BulkPdfResult } from '@/features/agent/provided_job_posts/hooks/usePdf';

const MARGIN_PX = 24 * 0.9;

/** ファイル名に使用できない文字を置換する */
const sanitizeFileName = (name: string): string => name.replace(/[/\\:*?"<>|]/g, '／');

export default function useSharedJobPostPdf() {
  const downloadPdf = async (id: number) => {
    const [pdfData] = await fetchPdfDataList([id]);
    if (!pdfData) throw new Error('求人票の取得に失敗しました');
    const blob = await generatePdfFromHtml(pdfData);

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sanitizeFileName(pdfData.companyName)}_${sanitizeFileName(pdfData.title)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadMultiplePdfs = async (ids: number[]): Promise<BulkPdfResult> => {
    if (ids.length === 0) return { requestedCount: 0, downloadedCount: 0, missingCount: 0 };

    const pdfDataList = await fetchPdfDataList(ids);
    if (pdfDataList.length === 0) throw new Error('求人票の取得に失敗しました');

    // 各求人票を個別PDFとして生成し、ZIPにまとめる
    const zip = new JSZip();
    for (const pdfData of pdfDataList) {
      const blob = await generatePdfFromHtml(pdfData);
      const fileName = sanitizeFileName(`${pdfData.companyName}_${pdfData.title}`);
      zip.file(`${fileName}.pdf`, blob);
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(zipBlob);
    const date = new Date().toISOString().split('T')[0];
    const a = document.createElement('a');
    a.href = url;
    a.download = `求人票_${date}_${pdfDataList.length}件.zip`;
    a.click();
    URL.revokeObjectURL(url);

    return {
      requestedCount: ids.length,
      downloadedCount: pdfDataList.length,
      missingCount: ids.length - pdfDataList.length,
    };
  };

  return { downloadPdf, downloadMultiplePdfs };
}

/** PDF描画に必要な項目だけを一括で取得する (Issue #3426) */
const fetchPdfDataList = async (ids: number[]): Promise<SharedJobPostPdfData[]> => {
  const query = ids.map((id) => `ids[]=${id}`).join('&');
  const res = await fetch(`/agent/api/shared_job_posts/pdf_data.json?${query}`);
  if (!res.ok) {
    throw new Error(`求人票の取得に失敗しました (status: ${res.status})`);
  }
  const data: AgentApiSharedJobPostsPdfIndex = await res.json();

  const byId = new Map(
    data.records.map((record) => [
      record.id,
      {
        title: record.title,
        companyName: record.company_name,
        providerCompanyName: record.provider_company_name,
        jobContent: record.job_content_json,
      },
    ])
  );

  // レスポンスはID昇順で返るため、要求順に並べ直す。解決できなかったIDはここで落ちる
  return ids
    .map((id) => byId.get(id))
    .filter((pdfData): pdfData is SharedJobPostPdfData => pdfData !== undefined);
};

/** オフスクリーンでReactコンポーネントをレンダリングし、html2canvasでキャプチャする */
const renderToCanvas = async (pdfData: SharedJobPostPdfData): Promise<HTMLCanvasElement> => {
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  document.body.appendChild(container);

  const root = createRoot(container);
  try {
    flushSync(() => {
      root.render(createElement(SharedJobPostPrintView, { data: pdfData }));
    });

    // scale: 2 で約4倍解像度のキャンバスを生成し、文字のジャギを大幅に低減する。
    // PDF へは PNG で埋め込むため、スケールアップによる劣化はほぼ無い。
    return await html2canvas(container.firstElementChild as HTMLElement, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    });
  } finally {
    root.unmount();
    document.body.removeChild(container);
  }
};

/** Canvasの内容をページ分割してjsPDFに追加する */
const addCanvasPagesToPdf = (
  pdf: jsPDF,
  canvas: HTMLCanvasElement,
  addPageBeforeFirst: boolean
): void => {
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();

  const pxPerMm = canvas.width / pdfWidth;
  const pageHeightPx = pdfHeight * pxPerMm;
  const contentHeightPx = pageHeightPx - MARGIN_PX * 2;

  let currentY = 0;
  let pageNumber = 0;

  while (currentY < canvas.height) {
    if (addPageBeforeFirst || pageNumber > 0) {
      pdf.addPage();
    }

    const remainingHeight = canvas.height - currentY;
    const sliceHeight = Math.min(contentHeightPx, remainingHeight);

    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = canvas.width;
    pageCanvas.height = MARGIN_PX * 2 + sliceHeight;

    const ctx = pageCanvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

      ctx.drawImage(
        canvas,
        0,
        currentY,
        canvas.width,
        sliceHeight,
        0,
        MARGIN_PX,
        canvas.width,
        sliceHeight
      );
    }

    // 文字を含むため可逆圧縮の PNG を使用。JPEG だとブロックノイズで文字エッジが滲む。
    const pageImgData = pageCanvas.toDataURL('image/png');
    const pageImgHeight = (pageCanvas.height * pdfWidth) / pageCanvas.width;
    pdf.addImage(pageImgData, 'PNG', 0, 0, pdfWidth, pageImgHeight, undefined, 'FAST');

    currentY += sliceHeight;
    pageNumber++;
  }
};

const generatePdfFromHtml = async (pdfData: SharedJobPostPdfData): Promise<Blob> => {
  const canvas = await renderToCanvas(pdfData);

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  addCanvasPagesToPdf(pdf, canvas, false);

  return pdf.output('blob');
};
